import { useState, useEffect } from 'react'
import { useGameStore } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import './BankScreen.css'

const PATIENCE_RESET_MS = 10 * 60 * 1000 // 10 minutes

// Grandma quotes database
const GRANDMA_SUCCESS_QUOTES = [
    { quote: "Hier, kauf dir was Schönes!", subtext: "Oma zwinkert verschwörerisch" },
    { quote: "Psst, erzähl es nicht deiner Mutter!", subtext: "Oma schaut sich um" },
    { quote: "Du siehst so dünn aus, kauf dir was zu essen!", subtext: "Oma kneift deine Wange" },
    { quote: "Früher hatten wir sowas nicht...", subtext: "Oma seufzt nostalgisch" },
    { quote: "Nimm, bevor Opa es sieht!", subtext: "Oma grinst verschmitzt" },
    { quote: "Mein Lieblingsenkelin!", subtext: "Du bist ihr einziges Enkelkind" },
    { quote: "Für gute Noten, oder?", subtext: "Oma hebt die Augenbraue" },
    { quote: "Das bleibt unter uns!", subtext: "Oma steckt es dir heimlich zu" },
    { quote: "Kauf dir davon keine Drogen!", subtext: "Oma meint es ernst" },
    { quote: "Ich hab noch was gefunden...", subtext: "Oma kramt in ihrer Handtasche" },
    { quote: "Du erinnerst mich an deinen Vater.", subtext: "Der hat auch immer gebettelt" },
    { quote: "Hier, aber nur dieses eine Mal!", subtext: "Das sagt sie jedes Mal" },
]

const GRANDMA_FAIL_QUOTES = [
    { quote: "Ich hab dir doch gerade erst was gegeben!", subtext: "Oma schüttelt den Kopf" },
    { quote: "Geld wächst nicht auf Bäumen!", subtext: "Oma verschränkt die Arme" },
    { quote: "Frag doch mal deine Eltern!", subtext: "Oma dreht sich weg" },
    { quote: "Nein heißt nein!", subtext: "Oma ist genervt" },
    { quote: "Du hast wohl den Hals nicht voll!", subtext: "Oma rollt mit den Augen" },
]

export function BankScreen() {
    const resources = useGameStore(state => state.resources)
    const loan = useGameStore(state => state.loan)
    const loanPaymentRate = useGameStore(state => state.loanPaymentRate)
    const grandmaLastAsk = useGameStore(state => state.grandmaLastAsk)
    const grandmaAngryUntil = useGameStore(state => state.grandmaAngryUntil)
    const takeLoan = useGameStore(state => state.takeLoan)
    const askGrandma = useGameStore(state => state.askGrandma)
    const setScreen = useGameStore(state => state.setScreen)
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const tutorialSubStep = useGameStore(state => state.tutorialSubStep)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const setTutorialSubStep = useGameStore(state => state.setTutorialSubStep)

    const [grandmaModal, setGrandmaModal] = useState<{
        show: boolean
        success: boolean
        amount?: number
        quote: string
        subtext: string
    } | null>(null)
    const [, forceUpdate] = useState(0)

    const euroBalance = resources.euro ?? 1000
    const hasActiveLoan = loan > 0

    // Force re-render every second to update timers
    useEffect(() => {
        const interval = setInterval(() => forceUpdate(n => n + 1), 1000)
        return () => clearInterval(interval)
    }, [])

    // Tutorial: Auto-scroll to Grandma
    useEffect(() => {
        if (tutorialPhase === 'bank' && tutorialSubStep === 1) {
            // Small delay to ensure render is complete
            const timer = setTimeout(() => {
                const omaBtn = document.getElementById('bank-grandma-btn')
                if (omaBtn) {
                    omaBtn.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [tutorialPhase, tutorialSubStep])

    // Calculate grandma patience
    const now = Date.now()
    const isGrandmaAngry = grandmaAngryUntil && now < grandmaAngryUntil
    const angryTimeLeft = isGrandmaAngry ? Math.ceil((grandmaAngryUntil - now) / 1000) : 0

    let successChance = 100
    let patienceTimeLeft = 0
    if (grandmaLastAsk && !isGrandmaAngry) {
        const timeSinceAsk = now - grandmaLastAsk
        if (timeSinceAsk < PATIENCE_RESET_MS) {
            successChance = Math.floor((timeSinceAsk / PATIENCE_RESET_MS) * 100)
            patienceTimeLeft = Math.ceil((PATIENCE_RESET_MS - timeSinceAsk) / 1000)
        }
    }

    // Calculate time to pay off loan
    const timeToPayOff = loanPaymentRate > 0 ? Math.ceil(loan / loanPaymentRate) : 0

    const handleAskGrandma = () => {
        const result = askGrandma()

        if (result.success) {
            const randomQuote = GRANDMA_SUCCESS_QUOTES[Math.floor(Math.random() * GRANDMA_SUCCESS_QUOTES.length)]
            setGrandmaModal({
                show: true,
                success: true,
                amount: result.amount,
                quote: randomQuote.quote,
                subtext: randomQuote.subtext
            })
        } else {
            const randomQuote = GRANDMA_FAIL_QUOTES[Math.floor(Math.random() * GRANDMA_FAIL_QUOTES.length)]
            setGrandmaModal({
                show: true,
                success: false,
                quote: randomQuote.quote,
                subtext: randomQuote.subtext
            })
        }

        // Tutorial: Advance to next sub-step
        if (tutorialPhase === 'bank' && tutorialSubStep === 1) {
            setTutorialSubStep(2)
        }
    }

    const closeGrandmaModal = () => {
        setGrandmaModal(null)

        // Tutorial: Advance to pvp phase after closing modal
        if (tutorialPhase === 'bank') {
            setTutorialPhase('pvp')
            setScreen('ranking')
        }
    }

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return `${m}:${s.toString().padStart(2, '0')}`
    }

    return (
        <GameLayout>
            <div className="bank-screen">
                {/* Grandma Modal */}
                {grandmaModal?.show && (
                    <div className="grandma-modal-overlay" onClick={closeGrandmaModal}>
                        <div className={`grandma-modal ${grandmaModal.success ? 'grandma-modal--success' : 'grandma-modal--fail'}`} onClick={e => e.stopPropagation()}>
                            <div className="grandma-modal__avatar">
                                <img src="/oma-avatar.png" alt="Oma" className="grandma-modal__avatar-img" />
                                <span className="grandma-modal__avatar-status">
                                    {grandmaModal.success ? '💕' : '😤'}
                                </span>
                            </div>
                            <div className="grandma-modal__quote">
                                "{grandmaModal.quote}"
                            </div>
                            <div className="grandma-modal__subtext">
                                {grandmaModal.subtext}
                            </div>
                            {grandmaModal.success && grandmaModal.amount && (
                                <div className="grandma-modal__amount">
                                    +{grandmaModal.amount.toFixed(2)}€
                                </div>
                            )}
                            <button
                                id="bank-grandma-ok-btn"
                                className="grandma-modal__close"
                                onClick={closeGrandmaModal}
                            >
                                {grandmaModal.success ? 'Danke, Oma! 🥰' : 'Okay... 😔'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Hero Header */}
                <div className="bank__hero">
                    <div className="bank__hero-bg"></div>
                    <span className="bank__hero-icon">🏦</span>
                    <h1 className="bank__hero-title">PAY4LOSER BANK</h1>
                    <p className="bank__hero-tagline">„Dein Partner für schnelle Liquidität"</p>
                </div>

                {/* Account Overview Cards */}
                <div className="bank__overview">
                    {/* Balance */}
                    <div className={`bank__stat bank__stat--balance ${euroBalance < 0 ? 'bank__stat--negative' : ''}`}>
                        <span className="bank__stat-icon">💶</span>
                        <div className="bank__stat-content">
                            <span className="bank__stat-label">Kontostand</span>
                            <span className="bank__stat-value">{euroBalance.toFixed(2)} €</span>
                        </div>
                    </div>

                    {/* Debt */}
                    <div className={`bank__stat bank__stat--debt ${loan > 0 ? 'bank__stat--active' : ''}`}>
                        <span className="bank__stat-icon">📊</span>
                        <div className="bank__stat-content">
                            <span className="bank__stat-label">Offene Kredite</span>
                            <span className="bank__stat-value">{loan > 0 ? `${loan.toFixed(2)} €` : '0.00 €'}</span>
                            {loan > 0 && (
                                <span className="bank__stat-sub">{loanPaymentRate.toFixed(2)}€/min • ~{timeToPayOff} Min.</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Loan Section */}
                <div className="bank__section bank__section--loan">
                    <div className="bank__section-title">
                        <span>💳</span>
                        <span>Sofortkredit</span>
                        {hasActiveLoan && <span className="bank__loan-active-badge">Kredit aktiv</span>}
                    </div>

                    {hasActiveLoan ? (
                        <div className="bank__loan-active">
                            <div className="bank__loan-active-header">
                                <span className="bank__loan-active-icon">⏳</span>
                                <span>Kredit wird abbezahlt...</span>
                            </div>

                            <div className="bank__loan-progress">
                                <div className="bank__loan-progress-info">
                                    <span>Restschuld: {loan.toFixed(2)}€</span>
                                    <span className="bank__loan-eta">~{timeToPayOff} Min.</span>
                                </div>
                                <div className="bank__loan-progress-bar">
                                    <div
                                        className="bank__loan-progress-fill"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="bank__loan-progress-rate">
                                    {loanPaymentRate.toFixed(2)}€ werden jede Minute abgebucht
                                </div>
                            </div>

                            <p className="bank__loan-active-hint">
                                Neuer Kredit möglich sobald dieser abbezahlt ist
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="bank__loan-benefits">
                                <div className="bank__benefit">
                                    <span>✓</span>
                                    <span>Sofortige Auszahlung auf dein Konto</span>
                                </div>
                                <div className="bank__benefit">
                                    <span>✓</span>
                                    <span>Bequeme Rückzahlung in kleinen Raten</span>
                                </div>
                                <div className="bank__benefit">
                                    <span>✓</span>
                                    <span>Keine versteckten Gebühren*</span>
                                </div>
                            </div>

                            <div className="bank__loan-options">
                                <button className="bank__loan-btn" onClick={() => takeLoan(500)}>
                                    <span className="bank__loan-get">500€</span>
                                    <span className="bank__loan-detail">Rückzahlung: 750€</span>
                                </button>
                                <button className="bank__loan-btn bank__loan-btn--featured" onClick={() => takeLoan(1000)}>
                                    <span className="bank__loan-badge">EMPFOHLEN</span>
                                    <span className="bank__loan-get">1.000€</span>
                                    <span className="bank__loan-detail">Rückzahlung: 1.500€</span>
                                </button>
                                <button className="bank__loan-btn" onClick={() => takeLoan(2000)}>
                                    <span className="bank__loan-get">2.000€</span>
                                    <span className="bank__loan-detail">Rückzahlung: 3.000€</span>
                                </button>
                            </div>

                            <p className="bank__fine-print">*effektiver Jahreszins: 4.999,99%</p>
                        </>
                    )}
                </div>

                {/* Grandma Section */}
                <div className="bank__section bank__section--grandma">
                    <div className="bank__section-header">
                        <img src="/oma-avatar.png" alt="Oma" className="bank__grandma-avatar" />
                        <div className="bank__section-title-group">
                            <span className="bank__section-title">Alternative Finanzierung</span>
                            <p className="bank__grandma-desc">
                                Frag Oma nach einem kleinen Zuschuss (50€ - 200€)
                            </p>
                        </div>
                    </div>

                    {/* Patience Timer */}
                    {!isGrandmaAngry && successChance < 100 && (
                        <div className="bank__patience">
                            <div className="bank__patience-header">
                                <span>⏱️ Omas Geduld erholt sich</span>
                                <span className="bank__patience-timer">{formatTime(patienceTimeLeft)}</span>
                            </div>
                            <div className="bank__patience-bar">
                                <div
                                    className="bank__patience-fill"
                                    style={{ width: `${successChance}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Angry State */}
                    {isGrandmaAngry && (
                        <div className="bank__grandma-angry">
                            <span className="bank__angry-emoji">😠</span>
                            <div className="bank__angry-content">
                                <span className="bank__angry-title">Oma braucht eine Pause</span>
                                <span className="bank__angry-timer">⏱️ {formatTime(angryTimeLeft)}</span>
                            </div>
                        </div>
                    )}

                    <button
                        id="bank-grandma-btn"
                        className={`bank__grandma-btn ${isGrandmaAngry ? 'bank__grandma-btn--disabled' : ''}`}
                        onClick={handleAskGrandma}
                        disabled={!!isGrandmaAngry}
                    >
                        {isGrandmaAngry ? (
                            <>👵 Oma schmollt...</>
                        ) : (
                            <>🙏 Oma fragen <span className="bank__btn-chance">{successChance}%</span></>
                        )}
                    </button>
                </div>

                <button className="bank__back" onClick={() => setScreen('city')}>
                    ← Zurück zur Stadt
                </button>
            </div>
        </GameLayout>
    )
}
